import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import ShopLogo from "../images/shop_logo.png";
import { SHOP_NAME } from "../Variables.js";

const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

const sortByCode = (a, b) => {
  const codeA = (a.code || '').toString();
  const codeB = (b.code || '').toString();
  return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
};

const categorizeAccounts = (accounts, balances) => {
  const categories = {
    currentAssets: [],
    fixedAssets: [],
    otherAssets: [],
    currentLiabilities: [],
    longTermLiabilities: [],
    equity: []
  };

  accounts.forEach(account => {
    if (!account.active) return;
    const balance = balances[account.id] || 0;
    if (Math.abs(balance) < 0.01) return;

    const name = (account.name || '').toLowerCase();
    const type = (account.type || '').toLowerCase();

    const accountData = { ...account, balance: Math.abs(balance) };

    if (balance > 0) { // Assets
      if (name.includes('cash') || name.includes('bank') || name.includes('receivable') || name.includes('inventory') || name.includes('prepaid') || type.includes('current asset')) {
        categories.currentAssets.push({ ...accountData, balance });
      } else if (name.includes('equipment') || name.includes('vehicle') || name.includes('building') || name.includes('property') || name.includes('fixed')) {
        categories.fixedAssets.push({ ...accountData, balance });
      } else {
        categories.otherAssets.push({ ...accountData, balance });
      }
    } else { // Liabilities & Equity (negative balances)
      if (name.includes('payable') || name.includes('accrued') || name.includes('tax payable') || type.includes('current liability')) {
        categories.currentLiabilities.push(accountData);
      } else if (name.includes('loan') || name.includes('mortgage') || type.includes('long term')) {
        categories.longTermLiabilities.push(accountData);
      } else if (name.includes('capital') || name.includes('equity') || name.includes('retained') || name.includes('drawing') || name.includes('owner')) {
        categories.equity.push(accountData);
      } else {
        categories.currentLiabilities.push(accountData);
      }
    }
  });

  // Sort all categories
  Object.keys(categories).forEach(key => {
    categories[key].sort(sortByCode);
  });

  return categories;
};

const calculateFinancialRatios = (balanceSheetData) => {
  const { totals } = balanceSheetData;
  const inventory = balanceSheetData.categorizedAccounts.currentAssets
    .find(a => (a.name || '').toLowerCase().includes('inventory'))?.balance || 0;

  const currentAssets = totals.totalCurrentAssets;
  const currentLiabilities = totals.totalCurrentLiabilities || 0.01;
  const totalAssets = totals.totalAssets || 0.01;
  const totalLiabilities = totals.totalLiabilities;
  const totalEquity = totals.totalEquity || 0.01;
  const fixedAssets = totals.totalFixedAssets;

  const currentRatio = currentAssets / currentLiabilities;
  const quickRatio = (currentAssets - inventory) / currentLiabilities;
  const debtRatio = totalLiabilities / totalAssets;
  const debtToEquity = totalLiabilities / totalEquity;
  const equityRatio = totalEquity / totalAssets;
  const fixedToTotal = totalAssets > 0 ? fixedAssets / totalAssets : 0;

  return {
    currentRatio,
    quickRatio,
    debtRatio,
    debtToEquity,
    equityRatio,
    fixedAssetsToTotalAssets: fixedToTotal,
    workingCapital: currentAssets - currentLiabilities,
    netWorth: totalAssets - totalLiabilities
  };
};

const getRatioInterpretation = (name, value) => {
  const rules = {
    currentRatio: [
      { min: 2.0, level: "excellent", msg: "Excellent liquidity – very strong position" },
      { min: 1.5, level: "good", msg: "Good liquidity – healthy buffer" },
      { min: 1.0, level: "caution", msg: "Adequate – monitor closely" },
      { min: 0, level: "poor", msg: "Risk of short-term payment issues" }
    ],
    quickRatio: [
      { min: 1.0, level: "excellent", msg: "Strong immediate liquidity" },
      { min: 0.8, level: "good", msg: "Good cash coverage" },
      { min: 0.5, level: "caution", msg: "Limited liquid reserves" },
      { min: 0, level: "poor", msg: "High risk without inventory sales" }
    ],
    debtRatio: [
      { max: 0.4, level: "excellent", msg: "Very low debt – conservative" },
      { max: 0.6, level: "good", msg: "Moderate debt – manageable" },
      { max: 0.8, level: "caution", msg: "High debt – increased risk" },
      { max: 1.0, level: "poor", msg: "Very high leverage – risky" }
    ],
    debtToEquity: [
      { max: 0.5, level: "excellent", msg: "Minimal leverage" },
      { max: 1.0, level: "good", msg: "Balanced financing" },
      { max: 2.0, level: "caution", msg: "High leverage" },
      { max: Infinity, level: "poor", msg: "Very high financial risk" }
    ],
    equityRatio: [
      { min: 0.5, level: "excellent", msg: "Strong owner funding" },
      { min: 0.4, level: "good", msg: "Good equity base" },
      { min: 0.3, level: "caution", msg: "Moderate equity" },
      { min: 0, level: "poor", msg: "Weak equity position" }
    ]
  };

  const list = rules[name] || rules.currentRatio;
  for (const rule of list) {
    if ('min' in rule && value >= rule.min) return { level: rule.level, message: rule.msg };
    if ('max' in rule && value <= rule.max) return { level: rule.level, message: rule.msg };
  }
  return { level: "poor", message: "Unfavorable ratio" };
};

const FinancialRatios = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [balanceSheetData, setBalanceSheetData] = useState(null);
  const [financialRatios, setFinancialRatios] = useState(null);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [isFetching, setIsFetching] = useState(false);

  const accountsCollectionRef = collection(db, "accounts");
  const journalEntriesCollectionRef = collection(db, "journalEntries");

  useEffect(() => {
    if (!user || !userData) {
      setLoading(true);
      return;
    }
    setLoading(false);
    fetchAccounts();
  }, [user, userData]);

  useEffect(() => {
    if (accounts.length > 0) generateFinancialData();
  }, [asOfDate, accounts]);

  const fetchAccounts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(accountsCollectionRef);
      const data = await getDocs(q);
      setAccounts(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (err) {
      setError("Failed to load accounts");
      setShowModal(true);
    }
  };

  const fetchJournalEntries = async () => {
    if (!user?.uid) return [];
    try {
      const q = query(journalEntriesCollectionRef);
      const data = await getDocs(q);
      return data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    } catch (err) {
      return [];
    }
  };

  const calculateBalanceAsOf = (accountId, accountName, entries) => {
    let balance = 0;
    const cutoff = new Date(asOfDate);
    entries.forEach(entry => {
      if (new Date(entry.date) <= cutoff) {
        entry.lines.forEach(line => {
          if (line.accountName === accountName || line.accountId === accountId) {
            balance += (Number(line.debit) || 0) - (Number(line.credit) || 0);
          }
        });
      }
    });
    return balance;
  };

  const generateFinancialData = async () => {
    if (accounts.length === 0) return;
    setIsFetching(true);

    try {
      const entries = await fetchJournalEntries();
      const balances = {};
      accounts.forEach(acc => {
        balances[acc.id] = calculateBalanceAsOf(acc.id, acc.name, entries);
      });

      const categorized = categorizeAccounts(accounts, balances);

      const totalCurrentAssets = categorized.currentAssets.reduce((s, a) => s + a.balance, 0);
      const totalFixedAssets = categorized.fixedAssets.reduce((s, a) => s + a.balance, 0);
      const totalOtherAssets = categorized.otherAssets.reduce((s, a) => s + a.balance, 0);
      const totalAssets = totalCurrentAssets + totalFixedAssets + totalOtherAssets;

      const totalCurrentLiabilities = categorized.currentLiabilities.reduce((s, a) => s + a.balance, 0);
      const totalLongTermLiabilities = categorized.longTermLiabilities.reduce((s, a) => s + a.balance, 0);
      const totalLiabilities = totalCurrentLiabilities + totalLongTermLiabilities;

      const totalEquityBeforeRE = categorized.equity.reduce((s, a) => s + a.balance, 0);
      const retainedEarnings = totalAssets - totalLiabilities - totalEquityBeforeRE;

      const finalEquity = totalEquityBeforeRE + (Math.abs(retainedEarnings) > 0.01 ? retainedEarnings : 0);
      const isBalanced = Math.abs(totalAssets - (totalLiabilities + finalEquity)) < 0.01;

      const finalData = {
        categorizedAccounts: categorized,
        totals: {
          totalCurrentAssets,
          totalFixedAssets,
          totalOtherAssets,
          totalAssets,
          totalCurrentLiabilities,
          totalLongTermLiabilities,
          totalLiabilities,
          totalEquity: finalEquity,
          totalLiabilitiesAndEquity: totalLiabilities + finalEquity,
          isBalanced
        }
      };

      setBalanceSheetData(finalData);
      setFinancialRatios(calculateFinancialRatios(finalData));

    } catch (err) {
      setError("Failed to calculate ratios");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  const generatePDF = () => {
    if (!financialRatios || !balanceSheetData) return;

    const doc = new jsPDF();
    const { totals } = balanceSheetData;
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    let y = 10;
    try { doc.addImage(ShopLogo, "PNG", 14, y, 15, 15); } catch (e) {}
    doc.setFontSize(16).setFont("helvetica", "bold").text(SHOP_NAME, 35, y + 5);
    doc.setFontSize(14).setFont("helvetica", "normal").text("Financial Ratios Analysis", 35, y + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, y + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, y + 23);
    doc.text(`As of ${dateFormatter.format(new Date(asOfDate))}`, 35, y + 28);
    y += 35;

    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const tableWidth = pageWidth - 2 * margin;

    const keyFigures = [
      ['Total Assets', `N${totals.totalAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
      ['Total Liabilities', `N${totals.totalLiabilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
      ['Total Equity', `N${totals.totalEquity.toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
      ['Current Assets', `N${totals.totalCurrentAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
      ['Current Liabilities', `N${totals.totalCurrentLiabilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
      ['Working Capital', `N${financialRatios.workingCapital.toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
      ['Net Worth', `N${financialRatios.netWorth.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]
    ];

    autoTable(doc, {
      startY: y,
      head: [[{ content: 'KEY FINANCIAL FIGURES', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [41, 128, 185], textColor: 255 } }]],
      body: keyFigures,
      theme: 'grid',
      styles: { fontSize: 10, textColor: 0, },
      columnStyles: { 0: { cellWidth: tableWidth * 0.6 }, 1: { cellWidth: tableWidth * 0.4, halign: 'right' } },
      margin: { left: margin, right: margin }
    });

    y = doc.lastAutoTable.finalY + 10;

    const liquidity = [
      ['Current Ratio', financialRatios.currentRatio.toFixed(2), getRatioInterpretation('currentRatio', financialRatios.currentRatio).message],
      ['Quick Ratio', financialRatios.quickRatio.toFixed(2), getRatioInterpretation('quickRatio', financialRatios.quickRatio).message]
    ];

    autoTable(doc, {
      startY: y,
      head: [[{ content: 'LIQUIDITY RATIOS', colSpan: 3, styles: { fillColor: [52, 152, 219], textColor: 255 } }]],
      body: liquidity,
      theme: 'grid',
      styles: {textColor: 0,},
      columnStyles: { 0: { cellWidth: tableWidth * 0.35 }, 1: { cellWidth: tableWidth * 0.15, halign: 'center' }, 2: { cellWidth: tableWidth * 0.5 } },
      margin: { left: margin, right: margin }
    });

    y = doc.lastAutoTable.finalY + 10;

    const leverage = [
      ['Debt Ratio', (financialRatios.debtRatio * 100).toFixed(1) + '%', getRatioInterpretation('debtRatio', financialRatios.debtRatio).message],
      ['Debt to Equity', financialRatios.debtToEquity.toFixed(2), getRatioInterpretation('debtToEquity', financialRatios.debtToEquity).message],
      ['Equity Ratio', (financialRatios.equityRatio * 100).toFixed(1) + '%', getRatioInterpretation('equityRatio', financialRatios.equityRatio).message]
    ];

    autoTable(doc, {
      startY: y,
      head: [[{ content: 'LEVERAGE RATIOS', colSpan: 3, styles: { fillColor: [231, 76, 60], textColor: 255 } }]],
      body: leverage,
      theme: 'grid',
      styles: {textColor: 0,},
      columnStyles: { 0: { cellWidth: tableWidth * 0.35 }, 1: { cellWidth: tableWidth * 0.15, halign: 'center' }, 2: { cellWidth: tableWidth * 0.5 } },
      margin: { left: margin, right: margin }
    });

    doc.save(`FinancialRatios_${asOfDate}.pdf`);
  };

  const refresh = () => {
    generateFinancialData();
    setSuccessMessage("Ratios refreshed successfully");
    setShowModal(true);
  };

  if (loading) return <Loader text="Loading" />;
  if (!userData) return <Loader text="Reading" />;

  const allowedRoles = ['Accountant', 'Admin', 'Supreme'];

  return (
    <>
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Financial Ratios</h2>
            <Link to="#" className="home-link" onClick={refresh}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed">
            {/* Controls */}
            <div className="row gap-15 align-center justify-center" style={{ marginTop: '10px'}}>
              <div className="row gap-1 align-center">
                <input
                  type="date"
                  className="input-field"
                  value={asOfDate}
                  onChange={(e) => setAsOfDate(e.target.value)}
                  style={{ marginBottom: '0px' }}
                />
              </div>
              <button 
                className="login-button login-button-d caps" 
                onClick={generatePDF}
                disabled={!financialRatios}
                style={{ marginTop: '0px' }}
              >
                DOWNLOAD
              </button>
            </div>

          {/* Financial Ratios Display */}
            {!isFetching && financialRatios && balanceSheetData && (
              <div className="scroll-fixed scroll-fixed3" style={{ width: '100%', maxWidth: '900px', padding: '20px' }}>
                {/* Key Financial Figures */}
                <div style={{ 
                  backgroundColor: '#d4edda',
                  color: '#155724',
                  padding: '20px',
                  borderRadius: '10px',
                  marginBottom: '20px',
                  textAlign: 'center',
                  border: '1px solid #c3e6cb'
                }}>
                  <h3 style={{ margin: '0 0 20px' }}>KEY FINANCIAL FIGURES</h3>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                    gap: '15px', 
                    marginTop: '15px' 
                  }}>
                    {[
                      ['Total Assets', balanceSheetData.totals.totalAssets],
                      ['Total Liabilities', balanceSheetData.totals.totalLiabilities],
                      ['Total Equity', balanceSheetData.totals.totalEquity],
                      ['Working Capital', financialRatios.workingCapital]
                    ].map(([label, value]) => (
                      <div key={label}>
                        <div style={{ fontSize: '13px', opacity: 0.9 }}>{label}</div>
                        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                          N{Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Liquidity Ratios */}
                <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '10px', border: '2px solid #3498db', marginBottom: '20px' }}>
                  <h4 style={{ color: '#3498db', textAlign: 'center', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                    Liquidity Ratios
                  </h4>
                  {['currentRatio', 'quickRatio'].map(key => {
                    const val = financialRatios[key];
                    const interp = getRatioInterpretation(key, val);
                    return (
                      <div key={key} style={{ background: 'white', padding: '15px', margin: '10px 0', borderRadius: '8px', borderLeft: '5px solid #3498db' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                          {key === 'currentRatio' ? 'Current Ratio' : 'Quick Ratio'}
                        </div>
                        <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#27ae60' }}>
                          {val.toFixed(2)}
                        </div>
                        <div style={{ fontSize: '13px', color: '#7f8c8d', fontStyle: 'italic' }}>
                          {interp.message}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Leverage Ratios */}
                <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '10px', border: '2px solid #e74c3c' }}>
                  <h4 style={{ color: '#e74c3c', textAlign: 'center', borderBottom: '2px solid #e74c3c', paddingBottom: '10px' }}>
                    Leverage Ratios
                  </h4>
                  {[
                    { key: 'debtRatio', label: 'Debt Ratio', format: v => (v * 100).toFixed(1) + '%' },
                    { key: 'debtToEquity', label: 'Debt to Equity', format: v => v.toFixed(2) },
                    { key: 'equityRatio', label: 'Equity Ratio', format: v => (v * 100).toFixed(1) + '%' }
                  ].map(({ key, label, format }) => {
                    const val = financialRatios[key];
                    const interp = getRatioInterpretation(key, val);
                    return (
                      <div key={key} style={{ background: 'white', padding: '15px', margin: '10px 0', borderRadius: '8px', borderLeft: '5px solid #e74c3c' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{label}</div>
                        <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#e74c3c' }}>
                          {format(val)}
                        </div>
                        <div style={{ fontSize: '13px', color: '#7f8c8d', fontStyle: 'italic' }}>
                          {interp.message}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Net Worth & Other */}
                <div style={{ marginTop: '20px', textAlign: 'center', padding: '20px', background: '#008ce0', color: 'white', borderRadius: '10px' }}>
                  <h3 className="font-white">Net Worth: N{financialRatios.netWorth.toLocaleString('en-US', { minimumFractionDigits: 2 })}</h3>
                </div>
              </div>
            )}
          </div>

          {showModal && (
            <SuccessModal
              icon={error ? "xmark" : "check"}
              iconColor={error ? "red" : "green"}
              message={error || successMessage}
              onClose={() => {
                setError("");
                setSuccessMessage("");
                setShowModal(false);
              }}
            />
          )}
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">Access denied.</p>
      )}
    </>
  );
};

export default FinancialRatios;